/**
 * QWERTY finger mapping — classic typing tutor layout
 */

export const HOME_KEYS = new Set(['a', 's', 'd', 'f', 'j', 'k', 'l', ';']);

/** 4-row keyboard (matches classic tutor visual) */
export const KEYBOARD_ROWS = [
  ['`', '1', '2', '3', '4', '5', '6', '7', '8', '9', '0', '-', '='],
  ['q', 'w', 'e', 'r', 't', 'y', 'u', 'i', 'o', 'p', '[', ']', '\\'],
  ['a', 's', 'd', 'f', 'g', 'h', 'j', 'k', 'l', ';', "'"],
  ['z', 'x', 'c', 'v', 'b', 'n', 'm', ',', '.', '/'],
];

/** All keys sharing the same finger as each home-row key */
export const HOME_FINGER_GROUPS = {
  a: ['a', 'q', 'z'],
  s: ['s', 'w', 'x'],
  d: ['d', 'e', 'c'],
  f: ['f', 'r', 'v', 't', 'g', 'b'],
  j: ['j', 'u', 'm', 'y', 'h', 'n'],
  k: ['k', 'i', ','],
  l: ['l', 'o', '.'],
  ';': [';', 'p', '/'],
};

const FINGER_META = {
  '`': { hand: 'left', finger: 'little', name: 'left little finger' },
  '1': { hand: 'left', finger: 'little', name: 'left little finger' },
  '2': { hand: 'left', finger: 'ring', name: 'left ring finger' },
  '3': { hand: 'left', finger: 'middle', name: 'left middle finger' },
  '4': { hand: 'left', finger: 'index', name: 'left index finger' },
  '5': { hand: 'left', finger: 'index', name: 'left index finger' },
  '6': { hand: 'right', finger: 'index', name: 'right index finger' },
  '7': { hand: 'right', finger: 'index', name: 'right index finger' },
  '8': { hand: 'right', finger: 'middle', name: 'right middle finger' },
  '9': { hand: 'right', finger: 'ring', name: 'right ring finger' },
  '0': { hand: 'right', finger: 'little', name: 'right little finger' },
  '-': { hand: 'right', finger: 'little', name: 'right little finger' },
  '=': { hand: 'right', finger: 'little', name: 'right little finger' },
  q: { hand: 'left', finger: 'little', name: 'left little finger' },
  w: { hand: 'left', finger: 'ring', name: 'left ring finger' },
  e: { hand: 'left', finger: 'middle', name: 'left middle finger' },
  r: { hand: 'left', finger: 'index', name: 'left index finger' },
  t: { hand: 'left', finger: 'index', name: 'left index finger' },
  y: { hand: 'right', finger: 'index', name: 'right index finger' },
  u: { hand: 'right', finger: 'index', name: 'right index finger' },
  i: { hand: 'right', finger: 'middle', name: 'right middle finger' },
  o: { hand: 'right', finger: 'ring', name: 'right ring finger' },
  p: { hand: 'right', finger: 'little', name: 'right little finger' },
  '[': { hand: 'right', finger: 'little', name: 'right little finger' },
  ']': { hand: 'right', finger: 'little', name: 'right little finger' },
  '\\': { hand: 'right', finger: 'little', name: 'right little finger' },
  a: { hand: 'left', finger: 'little', name: 'left little finger' },
  s: { hand: 'left', finger: 'ring', name: 'left ring finger' },
  d: { hand: 'left', finger: 'middle', name: 'left middle finger' },
  f: { hand: 'left', finger: 'index', name: 'left index finger' },
  g: { hand: 'left', finger: 'index', name: 'left index finger' },
  h: { hand: 'right', finger: 'index', name: 'right index finger' },
  j: { hand: 'right', finger: 'index', name: 'right index finger' },
  k: { hand: 'right', finger: 'middle', name: 'right middle finger' },
  l: { hand: 'right', finger: 'ring', name: 'right ring finger' },
  ';': { hand: 'right', finger: 'little', name: 'right little finger' },
  "'": { hand: 'right', finger: 'little', name: 'right little finger' },
  z: { hand: 'left', finger: 'little', name: 'left little finger' },
  x: { hand: 'left', finger: 'ring', name: 'left ring finger' },
  c: { hand: 'left', finger: 'middle', name: 'left middle finger' },
  v: { hand: 'left', finger: 'index', name: 'left index finger' },
  b: { hand: 'left', finger: 'index', name: 'left index finger' },
  n: { hand: 'right', finger: 'index', name: 'right index finger' },
  m: { hand: 'right', finger: 'index', name: 'right index finger' },
  ',': { hand: 'right', finger: 'middle', name: 'right middle finger' },
  '.': { hand: 'right', finger: 'ring', name: 'right ring finger' },
  '/': { hand: 'right', finger: 'little', name: 'right little finger' },
  ' ': { hand: 'thumb', finger: 'thumb', name: 'either thumb' },
};

const normalizeKey = (char) => {
  if (!char) return '';
  if (char === ' ') return ' ';
  return char.toLowerCase();
};

export const getFingerMeta = (char) => {
  const key = normalizeKey(char);
  return FINGER_META[key] || { hand: 'either', finger: 'index', name: 'correct finger' };
};

/** Find home-row anchor for any key in the same finger column */
export const getHomeKeyForChar = (char) => {
  const key = normalizeKey(char);
  const entry = Object.entries(HOME_FINGER_GROUPS).find(([, keys]) => keys.includes(key));
  return entry ? entry[0] : null;
};

/** Classic instruction: "Use your left little finger to strike both the 'a' and 'q'." */
export const getPairInstruction = (homeKey, extKey) => {
  const meta = getFingerMeta(homeKey);
  const home = normalizeKey(homeKey);
  const ext = normalizeKey(extKey);
  return `Use your ${meta.name} to strike both the '${home}' and '${ext}'.`;
};

export const getFingerInstruction = (char, lesson = null) => {
  if (lesson?.instructionText) {
    return lesson.instructionText;
  }

  if (lesson?.homeKey && lesson?.extKey) {
    return getPairInstruction(lesson.homeKey, lesson.extKey);
  }

  const key = normalizeKey(char);
  if (!key || key === ' ') {
    return 'Use either thumb to strike the space bar.';
  }

  if (lesson?.focusKeys?.length === 2) {
    const [k1, k2] = lesson.focusKeys;
    const home = getHomeKeyForChar(k1) || k1;
    if ([k1, k2].includes(key)) {
      const other = key === k1 ? k2 : k1;
      const homeAnchor = HOME_KEYS.has(home) ? home : getHomeKeyForChar(key) || home;
      const partner = key === homeAnchor ? other : homeAnchor;
      return getPairInstruction(homeAnchor, partner);
    }
  }

  const homeAnchor = getHomeKeyForChar(key);
  if (homeAnchor && HOME_FINGER_GROUPS[homeAnchor]) {
    const group = HOME_FINGER_GROUPS[homeAnchor].filter((k) => k !== homeAnchor);
    const top = group.find((k) => KEYBOARD_ROWS[1].includes(k));
    const bottom = group.find((k) => KEYBOARD_ROWS[3].includes(k));
    if (key === top && homeAnchor) {
      return getPairInstruction(homeAnchor, top);
    }
    if (key === bottom && homeAnchor) {
      return getPairInstruction(homeAnchor, bottom);
    }
  }

  const meta = getFingerMeta(key);
  return `Use your ${meta.name} to strike the '${key}' key.`;
};

export const getHighlightKeysForLesson = (lesson, currentChar) => {
  if (lesson?.focusKeys?.length) {
    const keys = [...lesson.focusKeys];
    const current = normalizeKey(currentChar);
    if (current && !keys.includes(current)) {
      keys.push(current);
    }
    return keys;
  }
  if (lesson?.homeKey && lesson?.extKey) {
    return [lesson.homeKey, lesson.extKey];
  }
  if (currentChar && currentChar !== ' ') {
    const home = getHomeKeyForChar(currentChar);
    if (home && HOME_FINGER_GROUPS[home]) {
      return HOME_FINGER_GROUPS[home].filter((k) =>
        KEYBOARD_ROWS[1].includes(k) ||
        KEYBOARD_ROWS[2].includes(k) ||
        KEYBOARD_ROWS[3].includes(k),
      );
    }
    return [normalizeKey(currentChar)];
  }
  return [];
};

export const HOME_INTRO_LINES = [
  'The highlighted keys are the home keys.',
  "The left hand is over 'ASDF'. The little finger types 'A', ring finger 'S', middle finger 'D', and index finger 'F'.",
  "The right hand is over 'JKL;'. The index finger types 'J', middle finger 'K', ring finger 'L', and little finger ';'.",
  'Each lesson pairs a home key with the top or bottom row key on the same finger — like aq, az, sw, sx.',
  'Press Space or click Start to begin the drills.',
];
